
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { getTranslationKeys } from './translationUtils';
import { TranslationsTable } from './TranslationsTable';
import { LanguageSettings } from './LanguageSettings';
import i18n from '@/lib/i18n';

export function TranslationsManager() {
  const { t, i18n: i18nInstance } = useTranslation();
  const [selectedLocale, setSelectedLocale] = useState<string>('en');
  const [availableLocales, setAvailableLocales] = useState<string[]>([]);
  const [translationKeys, setTranslationKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { updateSetting } = useSystemSettings();
  const isLoadingTranslationsRef = useRef(false);
  
  // Load all the translation data on component mount
  useEffect(() => {
    loadTranslations();
    
    // Listen for translation updates from other components
    const handleTranslationsUpdated = () => {
      // Skip if we're already loading translations to prevent loops
      if (isLoadingTranslationsRef.current) {
        console.log("Skipping translation reload - already in progress");
        return;
      }
      
      console.log("TranslationsManager: Detected translations update");
      loadTranslations();
    };
    
    document.addEventListener('translationsUpdated', handleTranslationsUpdated);
    
    return () => {
      document.removeEventListener('translationsUpdated', handleTranslationsUpdated);
    };
  }, []);
  
  const loadTranslations = async () => {
    // Set flag to prevent recursive loading
    if (isLoadingTranslationsRef.current) {
      console.log("Already loading translations, skipping");
      return;
    }
    
    isLoadingTranslationsRef.current = true;
    setIsLoading(true);
    
    try {
      // Try to reload translations from the database
      if (i18n.reloadResourcesFromDb) {
        await i18n.reloadResourcesFromDb();
      } else {
        await i18n.reloadResources();
      }
      
      // Get all available locales
      const resources = i18n.options?.resources || {};
      const locales = Object.keys(resources);
      setAvailableLocales(locales);
      
      if (locales.length > 0) {
        // If the selected locale isn't in available locales, default to the first one
        if (!locales.includes(selectedLocale)) {
          setSelectedLocale(locales[0]);
        }
        
        // Get all translation keys
        const keys = getTranslationKeys();
        setTranslationKeys(keys);
      }
    } catch (error) {
      console.error("Error loading translations:", error);
    } finally {
      setIsLoading(false);
      // Reset the loading flag with a small delay to prevent rapid sequential updates
      setTimeout(() => {
        isLoadingTranslationsRef.current = false;
      }, 500);
    }
  };
  
  const handleLocaleChange = async (locale: string) => {
    // Change the UI language immediately
    if (i18nInstance.changeLanguage) {
      await i18nInstance.changeLanguage(locale);
    }
    
    // Save the preference in system settings
    await updateSetting('default_locale', locale);
    setSelectedLocale(locale);
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('manageTranslations')}</CardTitle>
          <CardDescription>
            {t('manageApplicationTranslations')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="translations">
            <TabsList className="mb-4">
              <TabsTrigger value="translations">Translations</TabsTrigger>
              <TabsTrigger value="settings">Language Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="translations">
              <TranslationsTable 
                selectedLocale={selectedLocale}
                availableLocales={availableLocales}
                onLocaleChange={handleLocaleChange}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            </TabsContent>
            
            <TabsContent value="settings">
              <LanguageSettings 
                selectedLocale={selectedLocale}
                availableLocales={availableLocales}
                onLocaleChange={handleLocaleChange}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                translationKeys={translationKeys}
                reloadTranslations={loadTranslations}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
